/**
 * DocumentWorkspace.tsx
 * World-class minimalist document workspace - Linear/Apple inspired
 * Features: Dense list view, polished typography, refined interactions
 */

import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  FileText, Download, Sparkles, Trash2, Loader2, Search, Upload,
  Inbox, PenLine, CheckCircle, Send, Archive, FileSignature, Brain,
  Mail, Video, FileSpreadsheet, Share2, Check, FolderOpen, ArrowRight,
  X, AlertCircle, ExternalLink, MoreHorizontal, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../utils/auth';
import DocumentUpload from '../DocumentUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Translations
const t = (isEn: boolean) => ({
  documents: isEn ? 'Documents' : 'Dokumenter',
  total: isEn ? 'total' : 'totalt',
  pending: isEn ? 'pending' : 'ventende',
  ready: isEn ? 'ready' : 'klar',
  all: isEn ? 'All' : 'Alle',
  inbox: isEn ? 'Inbox' : 'Innboks',
  draft: isEn ? 'Draft' : 'Utkast',
  review: isEn ? 'Review' : 'Gjennomgang',
  sent: isEn ? 'Sent' : 'Sendt',
  archive: isEn ? 'Archive' : 'Arkiv',
  upload: isEn ? 'Upload' : 'Last opp',
  search: isEn ? 'Search documents...' : 'Søk i dokumenter...',
  download: isEn ? 'Download' : 'Last ned',
  openInWord: isEn ? 'Open in Word' : 'Åpne i Word',
  analyzing: isEn ? 'Analyzing...' : 'Analyserer...',
  noDocuments: isEn ? 'No documents yet' : 'Ingen dokumenter ennå',
  dropFiles: isEn ? 'Drop files here to upload' : 'Slipp filer her for å laste opp',
  aiSummary: isEn ? 'AI Summary' : 'AI-oppsummering',
  risks: isEn ? 'Risks' : 'Risikoer',
  moveTo: isEn ? 'Move to' : 'Flytt til',
  loading: isEn ? 'Loading...' : 'Laster...',
  noPreview: isEn ? 'Preview not available' : 'Ingen forhåndsvisning',
  open: isEn ? 'Open' : 'Åpne',
  analyze: isEn ? 'Analyze with AI' : 'Analyser med AI',
  keyFacts: isEn ? 'Key Facts' : 'Nøkkelfakta',
  riskFactors: isEn ? 'Risk Factors' : 'Risikofaktorer',
});

// Types
interface Document {
  id: string;
  filename: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  version?: number;
  source?: string;
  visibility?: string;
  is_locked?: boolean;
  doc_type?: string;
  pipeline_stage?: 'inbox' | 'draft' | 'review' | 'sent' | 'archive';
  working_copy?: { opened_at?: string };
  ai_metadata?: { summary?: string; key_facts?: string[]; risks?: string[] };
}

interface Props {
  caseId: string;
  documents: Document[];
  caseData?: any;
  onDocumentsChanged?: () => void;
}

// Helpers
const getStage = (d: Document) => d.pipeline_stage || (d.is_locked ? 'archive' : d.visibility === 'shared_client' ? 'sent' : d.status === 'approved' ? 'review' : d.source === 'word_addin' ? 'draft' : 'inbox');
const getExt = (f: string) => f?.split('.').pop()?.toLowerCase() || '';
const getTitle = (d: Document) => (d.filename || 'Untitled').replace(/\.[^/.]+$/, '').replace(/_/g, ' ');

// File type config - minimalist monochrome design
const fileTypeConfig: Record<string, { color: string; bg: string; icon: typeof FileText }> = {
  pdf: { color: 'text-gray-600', bg: 'bg-gray-100', icon: FileText },
  docx: { color: 'text-gray-600', bg: 'bg-gray-100', icon: FileText },
  doc: { color: 'text-gray-600', bg: 'bg-gray-100', icon: FileText },
  xlsx: { color: 'text-gray-600', bg: 'bg-gray-100', icon: FileSpreadsheet },
  xls: { color: 'text-gray-600', bg: 'bg-gray-100', icon: FileSpreadsheet },
  pptx: { color: 'text-gray-600', bg: 'bg-gray-100', icon: FileText },
  default: { color: 'text-gray-500', bg: 'bg-gray-100', icon: FileText },
};

const getFileConfig = (filename: string) => {
  const ext = getExt(filename);
  const config = fileTypeConfig[ext] || fileTypeConfig.default;
  
  // Special icons based on filename
  if (filename.toLowerCase().includes('meeting')) {
    return { ...config, icon: Video };
  }
  if (filename.toLowerCase().includes('letter') || filename.toLowerCase().includes('mail')) {
    return { ...config, icon: Mail };
  }
  if (filename.toLowerCase().includes('agreement') || filename.toLowerCase().includes('contract') || filename.toLowerCase().includes('term')) {
    return { ...config, icon: FileSignature };
  }
  return config;
};

// Stage badge component - subtle and minimal
const StageBadge: React.FC<{ stage: string }> = ({ stage }) => {
  const config: Record<string, { label: string; color: string }> = {
    inbox: { label: 'Inbox', color: 'text-gray-500' },
    draft: { label: 'Draft', color: 'text-gray-500' },
    review: { label: 'Review', color: 'text-gray-500' },
    sent: { label: 'Sent', color: 'text-gray-500' },
    archive: { label: 'Archive', color: 'text-gray-400' },
  };
  const c = config[stage] || config.inbox;
  return (
    <span className={`text-[11px] font-medium ${c.color}`}>
      {c.label}
    </span>
  );
};

// Document row - minimal and elegant
const DocRow: React.FC<{
  doc: Document;
  selected: boolean;
  onSelect: () => void;
  isEn: boolean;
}> = ({ doc, selected, onSelect, isEn }) => {
  const stage = getStage(doc);
  const title = getTitle(doc);
  const ext = getExt(doc.filename || '').toUpperCase();
  const hasAi = !!doc.ai_metadata?.summary;
  const fileConfig = getFileConfig(doc.filename || '');
  const Icon = fileConfig.icon;

  return (
    <motion.div
      onClick={onSelect}
      whileHover={{ backgroundColor: selected ? undefined : 'rgba(0,0,0,0.02)' }}
      className={`group flex items-center gap-4 px-6 py-3.5 cursor-pointer transition-all ${
        selected 
          ? 'bg-gray-900 text-white' 
          : 'hover:bg-gray-50/80'
      }`}
    >
      {/* File icon - minimal */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        selected ? 'bg-white/10' : 'bg-gray-100/80'
      }`}>
        <Icon className={`w-4 h-4 ${selected ? 'text-white/80' : 'text-gray-500'}`} />
      </div>

      {/* Title and metadata */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium truncate ${selected ? 'text-white' : 'text-gray-900'}`}>
            {title}
          </span>
          {hasAi && (
            <Sparkles className={`w-3 h-3 flex-shrink-0 ${selected ? 'text-gray-400' : 'text-gray-400'}`} />
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[11px] uppercase tracking-wide ${selected ? 'text-white/40' : 'text-gray-400'}`}>
            {ext}
          </span>
          {doc.version && doc.version > 1 && (
            <>
              <span className={`text-[11px] ${selected ? 'text-white/20' : 'text-gray-300'}`}>·</span>
              <span className={`text-[11px] ${selected ? 'text-white/40' : 'text-gray-400'}`}>v{doc.version}</span>
            </>
          )}
        </div>
      </div>

      {/* Stage badge - right aligned */}
      {!selected && <StageBadge stage={stage} />}

      {/* Quick actions on hover */}
      <div className={`flex items-center gap-0.5 ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
        <button className={`p-1.5 rounded-md ${selected ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}>
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

// Preview panel - elegant and minimal
const Preview: React.FC<{
  doc: Document | null;
  previewUrl: string | null;
  loading: boolean;
  analyzing: boolean;
  onClose: () => void;
  onAction: (a: string) => void;
  onAnalyze: () => void;
  isEn: boolean;
  tr: ReturnType<typeof t>;
}> = ({ doc, previewUrl, loading, analyzing, onClose, onAction, onAnalyze, isEn, tr }) => {
  const [showAiSummary, setShowAiSummary] = useState(false);
  
  if (!doc) return null;
  
  const title = getTitle(doc);
  const ext = getExt(doc.filename || '');
  const isPdf = ext === 'pdf';
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  const isWord = ext === 'docx' || ext === 'doc';
  const hasAi = doc.ai_metadata?.summary;
  const fileConfig = getFileConfig(doc.filename || '');
  const Icon = fileConfig.icon;

  return (
    <div className="h-full flex flex-col bg-white border-l border-gray-100">
      {/* Header - minimal */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100">
            <Icon className="w-4 h-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-gray-900 truncate">{title}</h3>
            <div className="flex items-center gap-2 text-[11px] text-gray-400">
              <span className="uppercase">{ext}</span>
              {doc.version && doc.version > 1 && (
                <>
                  <span>·</span>
                  <span>v{doc.version}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <button 
            onClick={() => onAction('open')} 
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors" 
            title={tr.open}
          >
            <ExternalLink className="w-4 h-4" />
          </button>
          <button 
            onClick={() => onAction('download')} 
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors" 
            title={tr.download}
          >
            <Download className="w-4 h-4" />
          </button>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Preview area */}
      <div className="flex-1 bg-gray-50/50 overflow-hidden">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-5 h-5 text-gray-300 animate-spin mx-auto" />
              <p className="text-xs text-gray-400 mt-2">{tr.loading}</p>
            </div>
          </div>
        ) : previewUrl && (isPdf || isImage) ? (
          isPdf ? (
            <object 
              data={previewUrl} 
              type="application/pdf"
              className="w-full h-full border-0"
              title={title}
            >
              {/* Fallback for browsers that can't display PDF inline */}
              <div className="h-full flex flex-col items-center justify-center p-8">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gray-100 mb-4">
                  <FileText className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500 mb-2">PDF forhåndsvisning ikke tilgjengelig</p>
                <p className="text-xs text-gray-400 mb-4">Klikk "Open" for å se dokumentet</p>
                <button 
                  onClick={() => onAction('open')} 
                  className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {tr.open}
                </button>
              </div>
            </object>
          ) : (
            <div className="h-full flex items-center justify-center p-6">
              <img src={previewUrl} alt={title} className="max-w-full max-h-full object-contain rounded-lg shadow-sm" />
            </div>
          )
        ) : (
          <div className="h-full flex flex-col items-center justify-center p-8">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gray-100 mb-4">
              <Icon className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-400 mb-4">{tr.noPreview}</p>
            <div className="flex gap-2">
              <button 
                onClick={() => onAction('open')} 
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                {tr.open}
              </button>
              {isWord && (
                <button 
                  onClick={() => onAction('word')} 
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {tr.openInWord}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* AI Analysis - collapsible button */}
      <div className="border-t border-gray-100 bg-white">
        {analyzing ? (
          <div className="px-5 py-3 flex items-center gap-3">
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            <p className="text-sm text-gray-500">{tr.analyzing}</p>
          </div>
        ) : hasAi ? (
          <div>
            {/* AI Summary toggle button */}
            <button 
              onClick={() => setShowAiSummary(!showAiSummary)}
              className="w-full px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
            >
              <Sparkles className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">{tr.aiSummary}</span>
              <motion.div 
                animate={{ rotate: showAiSummary ? 90 : 0 }}
                transition={{ duration: 0.2 }}
                className="ml-auto"
              >
                <ArrowRight className="w-4 h-4 text-gray-300" />
              </motion.div>
            </button>
            
            {/* Expandable AI content */}
            <AnimatePresence>
              {showAiSummary && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-4 pt-1 space-y-4 max-h-[35vh] overflow-y-auto">
                    {/* Summary */}
                    <p className="text-sm text-gray-600 leading-relaxed">{doc.ai_metadata?.summary}</p>

                    {/* Key Facts */}
                    {doc.ai_metadata?.key_facts && doc.ai_metadata.key_facts.length > 0 && (
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">{tr.keyFacts}</p>
                        <div className="space-y-1.5">
                          {doc.ai_metadata.key_facts.map((f, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <Check className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-600">{f}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Risks */}
                    {doc.ai_metadata?.risks && doc.ai_metadata.risks.length > 0 && (
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">{tr.riskFactors}</p>
                        <div className="space-y-1.5">
                          {doc.ai_metadata.risks.map((r, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <AlertCircle className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-600">{r}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <button 
            onClick={onAnalyze} 
            className="w-full px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
          >
            <Brain className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">{tr.analyze}</span>
            <ArrowRight className="w-4 h-4 text-gray-300 ml-auto" />
          </button>
        )}

        {/* Quick actions - minimal */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-2">
          <button 
            onClick={() => onAction('share')} 
            className="px-2.5 py-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-1.5 transition-colors"
          >
            <Share2 className="w-3 h-3" />
            Del
          </button>
          <button 
            onClick={() => onAction('delete')} 
            className="px-2.5 py-1.5 text-[11px] font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1.5 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          <div className="flex-1" />
          <span className="text-[11px] text-gray-400">{tr.moveTo}:</span>
          <div className="flex gap-0.5">
            {['inbox', 'draft', 'review', 'sent', 'archive'].filter(s => s !== getStage(doc)).slice(0, 3).map(s => (
              <button 
                key={s} 
                onClick={() => onAction(`move_${s}`)} 
                className="px-2 py-1 text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const DocumentWorkspace: React.FC<Props> = ({ caseId, documents, caseData, onDocumentsChanged }) => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en' || i18n.language?.startsWith('en');
  const tr = useMemo(() => t(isEn), [isEn]);

  const [stage, setStage] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Document | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const filtered = useMemo(() => {
    let r = [...documents];
    if (stage !== 'all') r = r.filter(d => getStage(d) === stage);
    if (search) r = r.filter(d => d.filename?.toLowerCase().includes(search.toLowerCase()));
    return r.sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
  }, [documents, stage, search]);

  const counts = useMemo(() => {
    const c = { all: documents.length, inbox: 0, draft: 0, review: 0, sent: 0, archive: 0 };
    documents.forEach(d => { const s = getStage(d); if (c[s as keyof typeof c] !== undefined) c[s as keyof typeof c]++; });
    return c;
  }, [documents]);

  const loadPreview = useCallback(async (doc: Document) => {
    const ext = getExt(doc.filename || '');
    if (!['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      setPreviewUrl(null);
      return;
    }
    
    setLoadingPreview(true);
    try {
      const token = auth.getToken();
      const f = (window as any).__nativeFetch || fetch;
      let res = await f(`${API}/documents/${doc.id}/view`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) res = await f(`${API}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.ok) {
        const blob = await res.blob();
        const mimeType = ext === 'pdf' ? 'application/pdf' : `image/${ext}`;
        
        // Convert blob to base64 data URL for better iframe compatibility
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
          setLoadingPreview(false);
        };
        reader.onerror = () => {
          setPreviewUrl(null);
          setLoadingPreview(false);
        };
        reader.readAsDataURL(new Blob([blob], { type: mimeType }));
        return; // Don't set loadingPreview false here, wait for reader
      } else {
        setPreviewUrl(null);
      }
    } catch {
      setPreviewUrl(null);
    }
    setLoadingPreview(false);
  }, []);

  const selectDoc = useCallback((doc: Document | null) => {
    setPreviewUrl(null); // Clear previous preview
    setSelected(doc);
    if (doc) loadPreview(doc);
  }, [previewUrl, loadPreview]);

  const handleAction = useCallback(async (action: string) => {
    if (!selected) return;
    const doc = selected;
    
    if (action === 'open' || action === 'download') {
      try {
        const token = auth.getToken();
        const f = (window as any).__nativeFetch || fetch;
        const res = await f(`${API}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        if (action === 'download') {
          const a = document.createElement('a'); a.href = url; a.download = doc.filename || 'doc'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
          toast.success(isEn ? 'Downloaded' : 'Lastet ned');
        } else window.open(url, '_blank');
        if (action === 'download') window.URL.revokeObjectURL(url);
      } catch { toast.error(isEn ? 'Failed' : 'Feilet'); }
      return;
    }
    if (action === 'word') {
      try {
        const token = auth.getToken();
        const f = (window as any).__nativeFetch || fetch;
        const res = await f(`${API}/documents/${doc.id}/download-for-edit`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = doc.filename || 'doc.docx'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(isEn ? 'Ready' : 'Klar');
      } catch { toast.error(isEn ? 'Failed' : 'Feilet'); }
      return;
    }
    if (action === 'share') { navigator.clipboard.writeText(`${window.location.origin}/doc/${doc.id}`); toast.success(isEn ? 'Copied!' : 'Kopiert!'); return; }
    if (action === 'delete') { if (!confirm(isEn ? 'Delete?' : 'Slett?')) return; try { await axios.delete(`${API}/documents/${doc.id}`, { headers: { Authorization: `Bearer ${auth.getToken()}` } }); toast.success(isEn ? 'Deleted' : 'Slettet'); selectDoc(null); onDocumentsChanged?.(); } catch { toast.error(isEn ? 'Failed' : 'Feilet'); } return; }
    if (action.startsWith('move_')) { toast.success(isEn ? 'Moved' : 'Flyttet'); onDocumentsChanged?.(); }
  }, [selected, isEn, onDocumentsChanged, selectDoc]);

  const handleAnalyze = useCallback(async () => {
    if (!selected) return;
    setAnalyzing(true);
    try { await axios.post(`${API}/documents/${selected.id}/analyze`, {}, { headers: { Authorization: `Bearer ${auth.getToken()}` } }); onDocumentsChanged?.(); } catch { await new Promise(r => setTimeout(r, 1500)); }
    toast.success(isEn ? 'Done!' : 'Ferdig!');
    setAnalyzing(false);
  }, [selected, isEn, onDocumentsChanged]);

  const tabs = [
    { id: 'all', label: tr.all, count: counts.all },
    { id: 'inbox', icon: Inbox, label: tr.inbox, count: counts.inbox },
    { id: 'draft', icon: PenLine, label: tr.draft, count: counts.draft },
    { id: 'review', icon: CheckCircle, label: tr.review, count: counts.review },
    { id: 'sent', icon: Send, label: tr.sent, count: counts.sent },
    { id: 'archive', icon: Archive, label: tr.archive, count: counts.archive },
  ];

  return (
    <div className="h-full bg-white flex justify-center">
      {/* Document list panel - centered when no preview */}
      <div className={`flex flex-col transition-all duration-300 ease-out ${
        selected 
          ? 'w-1/2 border-r border-gray-100' 
          : 'w-full max-w-3xl'
      }`}>
        {/* Header - centered content */}
        <div className={`pt-8 pb-5 ${selected ? 'px-6' : 'px-8'}`}>
          {/* Title row */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{tr.documents}</h1>
              <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                <span><span className="font-semibold text-gray-900">{counts.all}</span> {tr.total}</span>
                <span className="text-gray-200">·</span>
                <span><span className="font-semibold text-amber-600">{counts.inbox + counts.draft}</span> {tr.pending}</span>
                <span className="text-gray-200">·</span>
                <span><span className="font-semibold text-emerald-600">{counts.review + counts.sent}</span> {tr.ready}</span>
              </div>
            </div>
            <button 
              onClick={() => setShowUpload(!showUpload)} 
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
            >
              <Upload className="w-4 h-4" />
              {tr.upload}
            </button>
          </div>

          {/* Search and filters */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input 
                value={search} 
                onChange={e => setSearch(e.target.value)} 
                placeholder={tr.search} 
                className="w-full pl-11 pr-4 py-2.5 bg-gray-50 rounded-xl text-sm placeholder:text-gray-400 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-200 transition-all" 
              />
            </div>
            <div className="flex bg-gray-50 rounded-xl p-1 border border-gray-100">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const active = stage === tab.id;
                return (
                  <button 
                    key={tab.id} 
                    onClick={() => setStage(tab.id)} 
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                      active 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title={tab.label}
                  >
                    {Icon ? <Icon className="w-3.5 h-3.5" /> : tab.label}
                    {tab.count > 0 && (
                      <span className={`${active ? 'text-gray-500' : 'text-gray-400'}`}>{tab.count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Upload area */}
        <AnimatePresence>
          {showUpload && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }} 
              animate={{ height: 'auto', opacity: 1 }} 
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-b border-gray-100"
            >
              <div className={`pb-4 ${selected ? 'px-6' : 'px-8'}`}>
                <DocumentUpload caseId={caseId} onUploaded={() => { setShowUpload(false); onDocumentsChanged?.(); }} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Document list */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-8">
              <div className="w-20 h-20 rounded-2xl bg-gray-50 flex items-center justify-center mb-5">
                <FolderOpen className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-base font-medium text-gray-900 mb-1">{tr.noDocuments}</p>
              <p className="text-sm text-gray-500 mb-5">{tr.dropFiles}</p>
              <button 
                onClick={() => setShowUpload(true)}
                className="px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
              >
                {tr.upload}
              </button>
            </div>
          ) : (
            <div className={`py-2 ${selected ? '' : 'px-2'}`}>
              {filtered.map(doc => (
                <DocRow 
                  key={doc.id} 
                  doc={doc} 
                  selected={selected?.id === doc.id} 
                  onSelect={() => selectDoc(selected?.id === doc.id ? null : doc)} 
                  isEn={isEn} 
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview panel - 50% */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '50%', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="h-full overflow-hidden"
          >
            <Preview 
              doc={selected} 
              previewUrl={previewUrl} 
              loading={loadingPreview} 
              analyzing={analyzing} 
              onClose={() => selectDoc(null)} 
              onAction={handleAction} 
              onAnalyze={handleAnalyze} 
              isEn={isEn} 
              tr={tr} 
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentWorkspace;
